/*
  pointer-track.js — generic pointer position tracking for CSS.

  A [data-pointer-track] host writes the cursor position into CSS variables and
  renders nothing itself. Two shapes, because consumers want different things:

    --pointer-x  / --pointer-y    px, relative to the host's top-left.
                                  For things that sit ON the cursor.
    --pointer-nx / --pointer-ny   -1..1, relative to the host's center.
                                  For things that lean TOWARD the cursor.

  Consumers do all the rendering, and the follow-and-settle feel comes from a
  transition on the consumer's transform, not from JS easing. Two current uses:

    landing hero      -> nx/ny drive per-layer parallax depth
    showcase bottle   -> x/y park a circular cue on the cursor

  Host rects are cached: we write CSS variables to the same element every frame,
  so measuring per move would force a style recalc against writes we just made
  (the layout thrash card-tilt.js documents). Scroll and resize invalidate every
  host from ONE shared listener pair rather than a pair per host, which matters
  because the showcase mounts one host per bottle.

  Coarse pointers (touch, no hover) and prefers-reduced-motion are skipped.
*/
(function () {
  'use strict';

  const REDUCED =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const COARSE = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

  const hosts = [];

  function invalidateAll() {
    hosts.forEach(function (entry) {
      entry.rect = null;
    });
  }

  function track(el) {
    const entry = { el: el, rect: null, pending: null, ticking: false };
    hosts.push(entry);

    function write() {
      entry.ticking = false;
      const next = entry.pending;
      if (!next) return;
      el.style.setProperty('--pointer-x', next.x.toFixed(2) + 'px');
      el.style.setProperty('--pointer-y', next.y.toFixed(2) + 'px');
      el.style.setProperty('--pointer-nx', next.nx.toFixed(4));
      el.style.setProperty('--pointer-ny', next.ny.toFixed(4));
    }

    function schedule(next) {
      entry.pending = next;
      if (entry.ticking) return;
      entry.ticking = true;
      requestAnimationFrame(write);
    }

    el.addEventListener('pointermove', function (event) {
      if (event.pointerType === 'touch') return;
      if (!entry.rect) entry.rect = el.getBoundingClientRect();
      const rect = entry.rect;
      if (!rect.width || !rect.height) return;
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      schedule({
        x: x,
        y: y,
        nx: Math.max(-1, Math.min(1, (x / rect.width) * 2 - 1)),
        ny: Math.max(-1, Math.min(1, (y / rect.height) * 2 - 1))
      });
    });

    // Settle the lean back to neutral. The px pair is deliberately left where it
    // was: a cue parked on the cursor should fade out in place, not slide home.
    el.addEventListener('pointerleave', function () {
      if (!entry.pending) return;
      schedule({ x: entry.pending.x, y: entry.pending.y, nx: 0, ny: 0 });
    });
  }

  function init() {
    if (REDUCED || COARSE) return;
    document.querySelectorAll('[data-pointer-track]:not([data-pointer-bound])').forEach(function (el) {
      el.setAttribute('data-pointer-bound', 'true');
      track(el);
    });
    if (!hosts.length) return;
    // clientX/clientY are viewport-relative, so a stale rect skews the mapping.
    // Capture phase, on document: element scroll does not bubble, so a window
    // listener misses nested scrollers like the showcase row.
    document.addEventListener('scroll', invalidateAll, { capture: true, passive: true });
    window.addEventListener('resize', invalidateAll);
  }

  // Sections register this script themselves, so a page rendering more than one
  // consumer (the landing page has both the hero and the showcase) emits the
  // same src twice and executes it twice. Guard so hosts bind exactly once.
  if (!window.PointerTrack) {
    window.PointerTrack = { observe: track };
    document.addEventListener('DOMContentLoaded', init);
  }
})();
